import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AtSign, Hash } from "lucide-react";
import { api } from "@/lib/api";
import { avatarSrc } from "@/lib/defaults";

// @mentions et #hashtags partagés : rendu des « @Nom » / « #tag » cliquables (publications, commentaires)
// + autocomplétion de saisie (membres pour « @xx », hashtags les plus utilisés pour « #xx »).

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// « #tag » : lettres/chiffres/_ (1 à 40), précédé du début du texte ou d'un caractère non alphanumérique.
// (Sans lookbehind : le préfixe est capturé puis réinséré — compatible anciens WebView.)
const HASHTAG_RE = /(^|[^\p{L}\p{N}_#])#([\p{L}\p{N}_]{1,40})(?![\p{L}\p{N}_])/gu;

const linkStyle = { color: "var(--yalola-primary)" };

// Transforme les « #tag » d'un fragment de texte en liens vers la page du hashtag (les nombres seuls restent du texte).
const renderHashtags = (text, keyPrefix) => {
  const out = [];
  let last = 0;
  let m;
  let k = 0;
  HASHTAG_RE.lastIndex = 0;
  while ((m = HASHTAG_RE.exec(text)) !== null) {
    const tag = m[2];
    if (/^\d+$/.test(tag)) continue;
    const start = m.index + m[1].length;
    if (start > last) out.push(text.slice(last, start));
    out.push(
      <Link key={`${keyPrefix}h${k++}`} to={`/hashtag/${encodeURIComponent(tag.toLowerCase())}`} onClick={(e) => e.stopPropagation()} className="font-semibold hover:underline" style={linkStyle} data-testid={`hashtag-link-${tag.toLowerCase()}`}>
        #{tag}
      </Link>
    );
    last = m.index + m[0].length;
  }
  if (!out.length) return text;
  if (last < text.length) out.push(text.slice(last));
  return out;
};

// Transforme les « @Nom » présents dans `mentions` en liens vers le profil et les « #tag » en liens vers le hashtag.
export const renderMentions = (content, mentions) => {
  if (!content) return content;
  const list = (mentions || []).filter((m) => m && m.name && m.id);
  if (!list.length) return renderHashtags(content, "t");
  const names = list.map((m) => m.name).sort((a, b) => b.length - a.length).map(escapeRegex);
  const rx = new RegExp("@(" + names.join("|") + ")", "g");
  const out = [];
  let last = 0;
  let m;
  let k = 0;
  while ((m = rx.exec(content)) !== null) {
    if (m.index > last) out.push(renderHashtags(content.slice(last, m.index), `s${k}`));
    const mention = list.find((x) => x.name === m[1]);
    out.push(
      <Link key={`m${k++}`} to={`/profile/${mention.id}`} onClick={(e) => e.stopPropagation()} className="font-semibold hover:underline" style={linkStyle} data-testid={`mention-link-${mention.id}`}>
        @{m[1]}
      </Link>
    );
    last = m.index + m[0].length;
  }
  if (last < content.length) out.push(renderHashtags(content.slice(last), "e"));
  return out;
};

// Le « @requête » ou « #requête » en cours de frappe, en fin de texte.
// Lot 789 — « @prénom nom » accepté (un espace, deux mots) : les homonymes se départagent par le nom de famille.
export const MENTION_TRIGGER = /@([^\s@#]{2,20}(?: [^\s@#]{1,20})?)$/;
const HASHTAG_TRIGGER = /(^|[^\p{L}\p{N}_#])#([\p{L}\p{N}_]{1,40})$/u;

// Requête de mention en cours, ou null. Si le 1er mot est déjà une mention choisie (« @Marie b… »), on ne
// relance pas la recherche : la personne continue simplement sa phrase.
export const mentionQuery = (text, picked = []) => {
  const m = MENTION_TRIGGER.exec(text || "");
  if (!m) return null;
  const q = m[1];
  if (q.includes(" ") && picked.some((p) => p?.name === q.split(" ")[0])) return null;
  return q;
};

// Autocomplétion : membres dès que le texte se termine par « @xx », hashtags existants dès « #x » ;
// `pick` remplace la requête par « @Nom  » (et mémorise {id, name}) ou par « #tag  ».
export function useMentionAutocomplete({ text, setText, onPicked }) {
  const [results, setResults] = useState([]);
  const [kind, setKind] = useState(null); // "user" | "tag"
  const [open, setOpen] = useState(false);
  const reqRef = useRef(0);
  const pickedRef = useRef([]);
  useEffect(() => {
    const mq = mentionQuery(text, pickedRef.current);
    const hm = mq ? null : HASHTAG_TRIGGER.exec(text || "");
    if (!mq && !hm) { setOpen(false); setResults([]); setKind(null); return undefined; }
    const rid = ++reqRef.current;
    const tm = setTimeout(() => {
      const req = mq
        ? api.get("/search", { params: { q: mq } }).then(({ data }) => ({ kind: "user", items: (data.users || []).slice(0, 5) }))
        : api.get("/hashtags/suggest", { params: { q: hm[2] } }).then(({ data }) => {
          const typed = hm[2].toLowerCase();
          const items = (data.tags || []).slice(0, 6);
          // Le mot tapé est toujours proposé en premier s'il n'existe pas encore (nouveau hashtag).
          if (!/^\d+$/.test(typed) && !items.some((x) => x.tag === typed)) items.unshift({ tag: typed, count: 0, fresh: true });
          return { kind: "tag", items };
        });
      req.then((r) => {
        if (rid !== reqRef.current) return;
        setKind(r.kind);
        setResults(r.items);
        setOpen(true);
      }).catch(() => {});
    }, 250);
    return () => clearTimeout(tm);
  }, [text]);
  const pick = (item) => {
    if (kind === "tag") {
      setText((prev) => prev.replace(HASHTAG_TRIGGER, `$1#${item.tag} `));
    } else {
      setText((prev) => prev.replace(MENTION_TRIGGER, `@${item.name} `));
      pickedRef.current = [...pickedRef.current, { id: item.id, name: item.name }].slice(-20);
      onPicked?.({ id: item.id, name: item.name });
    }
    reqRef.current += 1;
    setOpen(false); setResults([]);
  };
  const close = () => { reqRef.current += 1; setOpen(false); setResults([]); };
  return { open: open && results.length > 0, kind, results, pick, close };
}

const fmtCount = (n) => (n >= 1000 ? `${Math.round(n / 100) / 10}k` : String(n));

// Liste des propositions (membres : avatar + nom ; hashtags : # + nombre de publications) sous le champ de saisie.
// Couleurs via les variables du thème (s'adapte au plein écran sombre grâce aux surcharges locales).
export const MentionSuggestions = ({ ac, testId = "mention-suggestions", className = "", t }) => {
  if (!ac?.open) return null;
  const tr = (k, fb) => (t ? t(k, fb) : fb);
  const isTag = ac.kind === "tag";
  const rowCls = "w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-[color:var(--yalola-surface-alt)] active:bg-[color:var(--yalola-surface-alt)] transition-colors";
  return (
    <div className={`rounded-xl border overflow-hidden shadow-lg ${className}`} style={{ background: "var(--yalola-surface)", borderColor: "var(--yalola-border)" }} role="listbox" data-testid={testId} data-kind={ac.kind}>
      <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}>
        {isTag ? <><Hash size={11} /> {tr("hashtag.suggest_label", "Hashtags")}</> : <><AtSign size={11} /> Mention</>}
      </div>
      {isTag ? ac.results.map((h) => (
        <button key={h.tag} type="button" role="option" onMouseDown={(e) => e.preventDefault()} onClick={() => ac.pick(h)} className={rowCls} style={{ color: "var(--yalola-text)" }} data-testid={`${testId}-tag-${h.tag}`}>
          <span className="h-8 w-8 rounded-full grid place-items-center shrink-0 font-bold" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}>#</span>
          <span className="min-w-0 flex-1">
            <span className="block font-semibold truncate">#{h.tag}</span>
            <span className="block text-[11px] truncate" style={{ color: "var(--yalola-text-muted)" }}>
              {h.fresh ? tr("hashtag.new", "Nouveau hashtag") : tr("hashtag.posts_count", "{n} publication(s)").replace("{n}", fmtCount(h.count))}
            </span>
          </span>
        </button>
      )) : ac.results.map((u) => (
        <button key={u.id} type="button" role="option" onMouseDown={(e) => e.preventDefault()} onClick={() => ac.pick(u)} className={rowCls} style={{ color: "var(--yalola-text)" }} data-testid={`${testId}-${u.id}`}>
          <img src={avatarSrc(u)} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="block font-semibold truncate">{u.name}</span>
            {u.city && <span className="block text-[11px] truncate" style={{ color: "var(--yalola-text-muted)" }}>{u.city}</span>}
          </span>
        </button>
      ))}
    </div>
  );
};
